
import { defineComponent, reactive, watch } from 'vue';
import Icone from '@/core/components/Icone.vue';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import ServicoCategoriaProduto from '@/servicos/Cadastros/Produtos/ServicoCategoriaProduto';
import { ICategoriaProdutos, ICategoriaProdutosEmpresa } from '@/models/Entidades/Cadastros/Produtos/ICategoriaProdutos';
import SelecionarCategoria from '@/components/Cadastros/Produtos/SelecionarCategoria.vue';
import SelecionarEmpresa from '@/components/MeuSistema/Empresas/SelecionarEmpresa.vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';

export default defineComponent({
  name: 'CategoriaProdutoModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    Icone,
    RequisicaoModal,
    SelecionarCategoria,
    SelecionarEmpresa,
  },
  emits: ['sincronizarRegistro', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes,
      preencherPermissoesDados, preencherEmpresasComEstrategiaPermissaoDados, apresentarMensagemSucesso, defineEmpresasSelecionadasCadastroCompartilhado,
      filtrarPermissaoDadosUsuarioMultiEmpresas, verificaConceitoParaApresentarEmpresas,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso,
      defineNovaOperacao, defineTextoPadraoBotoes, sincronizarRegistro,
    } = useModalBase(props, emit);
    const servicoCategoriaProduto = new ServicoCategoriaProduto();
    telaBase.identificadorRecurso = 'CADASTRO_CATEGORIAS_PRODUTOS';
    telaBase.identificadorPermissao = 'PER_CADASTRO_CATEGORIAS_PRODUTOS';

    const state = reactive({
      categoriaProduto: {} as ICategoriaProdutos,
    });

    function objetoInicial() {
      state.categoriaProduto = {} as ICategoriaProdutos;
      state.categoriaProduto.codigo = 0;
      state.categoriaProduto.ativo = true;
      state.categoriaProduto.empresas = [];
      telaBase.empresasSelecionadas = [];
    }

    function preparaPersistenciaEmpresas() {
      const categoriaProdutoEmpresas : ICategoriaProdutosEmpresa[] = [];
      if (telaBase.empresasSelecionadas.length > 0) {
        telaBase.empresasSelecionadas.forEach((codigoEmpresa) => {
          const empresaExistente = state.categoriaProduto.empresas.find((c) => c.codigoEmpresa === codigoEmpresa);
          if (empresaExistente !== undefined) {
            categoriaProdutoEmpresas.push(empresaExistente);
          } else {
            const categoriaProdutoEmpresa: ICategoriaProdutosEmpresa = { codigo: 0, codigoCategoriaProdutos: state.categoriaProduto.codigo, codigoEmpresa };
            categoriaProdutoEmpresas.push(categoriaProdutoEmpresa);
          }
        });
      }
      state.categoriaProduto.empresas = categoriaProdutoEmpresas;
    }

    async function salvar(salvarNovo: boolean) {
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };

      apresentarBarraProgresso();
      preparaPersistenciaEmpresas();
      if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
        retorno = await servicoCategoriaProduto.incluir(state.categoriaProduto);
      } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
        retorno = await servicoCategoriaProduto.alterar(state.categoriaProduto);
      }

      ocultarBarraProgresso();

      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
        } else {
          sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
        }
        apresentarMensagemSucesso(retorno.mensagem);
        if (salvarNovo) {
          objetoInicial();
          telaBase.empresasSelecionadas = [props.operacao.empresaSelecionada];
          defineNovaOperacao(props.operacao);
        } else {
          modalBase.computedVisivel = false;
        }
      } else {
        apresentarRetornoRequisicao(retorno);
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      objetoInicial();
      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) {
          await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        } else {
          await obterPermissoes(ETipoPermissao.Dados);
        }
        await preencherEmpresasComEstrategiaPermissaoDados(props.operacao.tipoPermissaoDados, true);

        defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          state.categoriaProduto = await servicoCategoriaProduto.obter(props.operacao.codigoRegistro, props.operacao.empresaSelecionada);
          if (UtilitarioGeral.validaLista(state.categoriaProduto.empresas)) {
            state.categoriaProduto.empresas.forEach((e) => {
              telaBase.empresasSelecionadas.push(e.codigoEmpresa);
            });
          }
        } else {
          await defineEmpresasSelecionadasCadastroCompartilhado(props.operacao.empresaSelecionada);
        }

        telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
        verificaConceitoParaApresentarEmpresas();
      }
      telaBase.carregando = false;
    });

    return {
      telaBase,
      props,
      modalBase,
      state,
      salvar,
      objetoInicial,
      EPermissaoDados,
    };
  },
});
